import { useState, useRef, useCallback } from "react";
import { DrawerOrDialog } from "./DrawerOrDialog.js";
import { TabsSegmented } from "./TabsSegmented.js";
import { UploadFileDropZone } from "./UploadFileDropZone.js";
import { Button } from "./Button.js";
import { Spinner } from "./Spinner.js";
import { cn } from "../utils/cn.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import {
  useEntityMediaUpload,
  type EntitySelection,
  type PhotoUpload
} from "../hooks/useEntityMediaUpload.js";
import { useIsTouchDevice } from "../hooks/useIsTouchDevice.js";
import {
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  LinkIcon
} from "@heroicons/react/24/outline";
import { Badge } from "./index.js";
import { AnimatePresence, motion } from "motion/react";

export interface UnassignedFile {
  id: number;
  document_id: number;
  url: string;
  thumbnail_url: string | null;
}

interface AddMediaModalProps {
  entity: EntitySelection;
  boardId: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  unassignedFiles?: UnassignedFile[];
  isLoadingUnassigned?: boolean;
}

type TabId = "upload" | "existing";

const AddMediaModal = ({
  entity,
  boardId,
  open,
  onOpenChange,
  onSuccess,
  unassignedFiles = [],
  isLoadingUnassigned = false
}: AddMediaModalProps) => {
  const [activeTab, setActiveTab] = useState<TabId>("upload");
  const [photos, setPhotos] = useState<PhotoUpload[]>([]);
  const [selectedExisting, setSelectedExisting] = useState<Set<number>>(
    new Set()
  );

  const isGummy = useIsGummy();
  const isTouchDevice = useIsTouchDevice();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTriggeredRef = useRef(false);
  const hasUnassigned = unassignedFiles.length > 0;
  const shouldAutoTrigger = isTouchDevice && !hasUnassigned;

  const {
    uploadBatch,
    assignExisting,
    isUploading,
    uploadProgress,
    errors,
    clearProgress,
    createPreviewUrl,
    revokePreviewUrl,
    isValidImageFile
  } = useEntityMediaUpload({
    boardId,
    onSuccess: () => {
      handleClose();
      onSuccess?.();
    }
  });

  const handleClose = () => {
    // Cleanup previews
    photos.forEach((p) => revokePreviewUrl(p.preview));
    setPhotos([]);
    setSelectedExisting(new Set());
    clearProgress();
    onOpenChange(false);
  };

  const handleFileDrop = ({
    acceptedFiles
  }: {
    acceptedFiles: File[];
    fileRejections: unknown[];
    event: unknown;
  }) => {
    const validFiles = acceptedFiles.filter(isValidImageFile);
    const newPhotos: PhotoUpload[] = validFiles.map((file) => ({
      file,
      preview: createPreviewUrl(file)
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      const photo = prev[index];
      if (photo) revokePreviewUrl(photo.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleExisting = (id: number) => {
    setSelectedExisting((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleUpload = async () => {
    if (photos.length === 0) return;
    await uploadBatch(photos, entity);
  };

  const handleAssign = async () => {
    const filesToAssign = unassignedFiles.filter((f) =>
      selectedExisting.has(f.id)
    );
    if (filesToAssign.length === 0) return;
    await assignExisting(filesToAssign, entity);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = Array.from(files).filter(isValidImageFile);
      const newPhotos: PhotoUpload[] = validFiles.map((file) => ({
        file,
        preview: createPreviewUrl(file)
      }));
      setPhotos((prev) => [...prev, ...newPhotos]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Callback ref: triggers file picker on mount for touch devices when no unassigned files
  const inputRefCallback = useCallback(
    (node: HTMLInputElement | null) => {
      fileInputRef.current = node;
      if (node && shouldAutoTrigger && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        node.click();
      }
    },
    [shouldAutoTrigger]
  );

  return (
    <DrawerOrDialog.Root
      drawer={{ modal: true, open, onOpenChange }}
      dialog={{ open, onOpenChange }}
      nested
    >
      <DrawerOrDialog.Content
        position="top"
        className={cn(
          "px-2 md:max-w-lg md:px-6",
          !isGummy && "dark:bg-mono-950 bg-white"
        )}
        innerClassName="flex flex-col"
      >
        {/* Hidden file input for touch device auto-trigger */}
        {shouldAutoTrigger && (
          <input
            ref={inputRefCallback}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
        )}
        <div className="contents">
          <DrawerOrDialog.Title>
            <div className="flex items-start gap-4 text-base">
              <div
                className={cn(
                  "mt-1 p-2",
                  isGummy
                    ? cn("rounded-surface-xs  bg-tile-media")
                    : "rounded-md bg-mono-100 dark:bg-mono-800 text-mono-500 dark:text-mono-400"
                )}
              >
                <PhotoIcon className="size-8 " />
              </div>
              <div>
                <h3
                  className={cn(
                    "text-xl",
                    isGummy
                      ? "font-extrabold text-mono-900 dark:text-white"
                      : "font-normal"
                  )}
                >
                  Add Media
                </h3>
                <p
                  className={cn(
                    "text-sm leading-tight",
                    isGummy
                      ? "font-extrabold text-mono-500 dark:text-mono-400"
                      : "text-muted-foreground"
                  )}
                >
                  {entity.name}
                </p>
              </div>
            </div>
          </DrawerOrDialog.Title>

          <div
            className={cn(
              "grid flex-1 gap-4 py-4 gap-6",
              !isGummy && "border-border border-t"
            )}
          >
            {/* Tabs */}
            {hasUnassigned && (
              <div
                className={cn(
                  "flex gap-1 p-1",
                  isGummy
                    ? cn(
                        "rounded-surface-md bg-white dark:bg-mono-900",
                        "shadow-card"
                      )
                    : "border-border rounded-lg bg-mono-100 dark:border dark:bg-transparent"
                )}
              >
                <TabsSegmented<TabId>
                  defaultValue={activeTab}
                  onValueChange={(val) => val && setActiveTab(val)}
                  className={cn(
                    isGummy
                      ? "rounded-surface-xs bg-tile-media"
                      : "border-border rounded-md bg-white shadow-sm dark:border dark:bg-mono-900"
                  )}
                >
                  <button
                    data-id="upload"
                    className={cn(
                      "vertical-center flex flex-1 items-center justify-center gap-2 px-4 py-2 text-sm",
                      isGummy
                        ? "font-extrabold text-mono-800 dark:text-mono-100 text-lg md:text-xl data-[checked=true]:text-white data-[checked=true]:text-shadow-on-color"
                        : "text-mono-600 dark:text-mono-300"
                    )}
                  >
                    <ArrowUpTrayIcon className=" size-6 mb-0.5 mr-2 inline" />
                    <span>Upload new</span>
                  </button>
                  <button
                    data-id="existing"
                    className={cn(
                      "whitespace-nowrap vertical-center gap-4 px-4 py-2 text-sm",
                      isGummy
                        ? "font-extrabold text-mono-800 dark:text-mono-100 text-lg md:text-xl data-[checked=true]:text-white data-[checked=true]:text-shadow-on-color"
                        : "text-mono-600 dark:text-mono-300",
                      `flex flex-1 items-center justify-center `
                    )}
                  >
                    <span className="flex items-center justify-center">
                      <LinkIcon className=" size-6 mb-0.5 mr-2 inline" />
                      Unlinked library
                      <Badge
                        count={unassignedFiles.length}
                        action="info"
                        className="static ml-1"
                      />
                    </span>
                  </button>
                </TabsSegmented>
              </div>
            )}

            {/* Upload Tab */}
            {activeTab === "upload" && (
              <div className="space-y-4">
                {photos.length === 0 && (
                  <UploadFileDropZone onDrop={handleFileDrop} />
                )}

                {/* Preview Grid */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((photo, index) => {
                      const fileId = `${photo.file.name}-${index}`;
                      const progress = uploadProgress.get(fileId);
                      const error = errors.get(fileId);

                      return (
                        <div
                          key={index}
                          className={cn(
                            "relative aspect-square overflow-hidden",
                            isGummy
                              ? "rounded-surface-xs"
                              : "border-mono-200 dark:border-mono-700 rounded-lg border"
                          )}
                        >
                          <img
                            src={photo.preview}
                            alt={photo.file.name}
                            className="h-full w-full object-cover"
                          />

                          {/* Remove button */}
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              aria-label="Remove"
                              className={cn(
                                "absolute right-1 top-1 inline-flex items-center justify-center text-white",
                                isGummy
                                  ? cn(
                                      "h-8 w-8 rounded-full",
                                      "bg-gradient-to-b from-mono-700 to-mono-900",
                                      "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.9]",
                                      "motion-reduce:transform-none motion-reduce:transition-none"
                                    )
                                  : "bg-mono-900/70 hover:bg-mono-900 rounded-full p-1"
                              )}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}

                          {/* Progress overlay */}
                          {progress !== undefined && progress < 100 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <Spinner className="h-6 w-6 text-white" />
                            </div>
                          )}

                          {/* Success indicator */}
                          {progress === 100 && !error && (
                            <div className="absolute inset-0 flex items-center justify-center bg-green-500/30">
                              <CheckCircleIcon className="h-8 w-8 text-green-500" />
                            </div>
                          )}

                          {/* Error indicator */}
                          {error && (
                            <div className="absolute inset-0 flex items-center justify-center bg-red-500/30">
                              <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Pick Existing Tab */}
            {activeTab === "existing" && (
              <div className="space-y-4">
                {isLoadingUnassigned ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : unassignedFiles.length === 0 ? (
                  <div className="text-mono-500 dark:text-mono-400 py-8 text-center">
                    All files have been linked
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {unassignedFiles.map((file) => {
                      const isSelected = selectedExisting.has(file.id);
                      return (
                        <button
                          key={file.id}
                          type="button"
                          onClick={() => toggleExisting(file.id)}
                          className={cn(
                            "relative aspect-square overflow-hidden transition-all",
                            isGummy
                              ? cn(
                                  "rounded-surface-xs",
                                  isSelected && "ring-4 ring-primary-400/50"
                                )
                              : cn(
                                  "rounded-lg border-2",
                                  isSelected
                                    ? "border-primary-500 ring-primary-500/30 ring-2"
                                    : "border-mono-200 hover:border-mono-300 dark:border-mono-700 dark:hover:border-mono-600"
                                )
                          )}
                        >
                          <img
                            src={file.thumbnail_url || file.url}
                            alt="Unassigned file"
                            className="h-full w-full object-cover"
                          />
                          {isSelected && (
                            <div
                              className={cn(
                                "absolute right-1 top-1 inline-flex items-center justify-center",
                                isGummy
                                  ? cn(
                                      "h-8 w-8 rounded-full",
                                      "bg-gradient-to-b from-primary-400 to-primary-600"
                                    )
                                  : "bg-primary-500 rounded-full p-1"
                              )}
                            >
                              <CheckIcon
                                className={cn(
                                  "size-5",
                                  isGummy
                                    ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]"
                                    : "text-primary-100"
                                )}
                              />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div
            className={cn(
              "flex flex-col gap-4 pb-4 pt-4 md:flex-row md:justify-end md:pb-0",
              !isGummy && "border-mono-200 dark:border-mono-700"
            )}
          >
            {isGummy ? (
              <button
                type="button"
                onClick={handleClose}
                className={cn(
                  "inline-flex h-12 items-center justify-center rounded-surface-md px-5 text-base font-black",
                  "bg-tile-neutral text-mono-800",
                  "dark:from-mono-800 dark:to-mono-900 dark:text-mono-100",
                  "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                  "motion-reduce:transform-none motion-reduce:transition-none",
                  "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
                  "w-full md:w-auto md:flex-none"
                )}
              >
                Cancel
              </button>
            ) : (
              <Button
                action="secondary"
                onClick={handleClose}
                className="flex-1 md:flex-none"
              >
                Cancel
              </Button>
            )}

            {activeTab === "upload" ? (
              isGummy ? (
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={photos.length === 0 || isUploading || !boardId}
                  className={cn(
                    "relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-surface-md px-5 text-base font-black",
                    "bg-gradient-to-b from-primary-400 to-primary-600 text-white",
                    "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                    "motion-reduce:transform-none motion-reduce:transition-none",
                    "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
                    "disabled:pointer-events-none disabled:opacity-50 disabled:grayscale",
                    isUploading && "cursor-wait",
                    "w-full md:w-auto md:flex-none"
                  )}
                  style={{ textShadow: "var(--text-shadow-on-color)" }}
                >
                  {isUploading ? (
                    <Spinner className="h-5 w-5" />
                  ) : (
                    <>
                      <span>Upload</span>
                      <AnimatePresence>
                        {photos.length > 0 && (
                          <motion.span
                            className="flex items-center justify-center"
                            initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                            animate={{
                              width: "auto",
                              opacity: 1,
                              marginLeft: 0
                            }}
                            exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                          >
                            <span
                              className={cn(
                                "inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-black",
                                "bg-white text-primary-700 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8),0_1px_2px_0_rgba(0,0,0,0.2)]"
                              )}
                              style={{ textShadow: "none" }}
                            >
                              {photos.length}
                            </span>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </button>
              ) : (
                <Button
                  usePrimaryColors={true}
                  action="primary"
                  onClick={handleUpload}
                  loading={isUploading}
                  disabled={photos.length === 0 || isUploading || !boardId}
                  innerClassName="flex items-center justify-center"
                >
                  <span>Upload</span>
                  <AnimatePresence>
                    {photos.length > 0 && (
                      <motion.span
                        className="flex items-center justify-center"
                        initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                        animate={{ width: "auto", opacity: 1, marginLeft: 8 }}
                        exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                      >
                        <Badge
                          count={photos.length}
                          action="info"
                          className=" bg-primary-400 border-primary-600/50 text-primary-100 dark:bg-primary-300 dark:border-primary-300 dark:text-primary-800 static"
                        />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              )
            ) : isGummy ? (
              <button
                type="button"
                onClick={handleAssign}
                disabled={
                  selectedExisting.size === 0 || isUploading || !boardId
                }
                className={cn(
                  "relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-surface-md px-5 text-base font-black",
                  "bg-gradient-to-b from-primary-400 to-primary-600 text-white",
                  "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                  "motion-reduce:transform-none motion-reduce:transition-none",
                  "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
                  "disabled:pointer-events-none disabled:opacity-50 disabled:grayscale",
                  isUploading && "cursor-wait",
                  "w-full md:w-auto md:flex-none"
                )}
                style={{ textShadow: "var(--text-shadow-on-color)" }}
              >
                {isUploading ? (
                  <Spinner className="h-5 w-5" />
                ) : (
                  <>
                    <span>Link</span>
                    <AnimatePresence>
                      {selectedExisting.size > 0 && (
                        <motion.span
                          className="flex items-center justify-center"
                          initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                          animate={{ width: "auto", opacity: 1, marginLeft: 0 }}
                          exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                        >
                          <span
                            className={cn(
                              "inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-black",
                              "bg-white text-primary-700 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8),0_1px_2px_0_rgba(0,0,0,0.2)]"
                            )}
                            style={{ textShadow: "none" }}
                          >
                            {selectedExisting.size}
                          </span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </button>
            ) : (
              <Button
                usePrimaryColors={true}
                action="primary"
                onClick={handleAssign}
                loading={isUploading}
                disabled={
                  selectedExisting.size === 0 || isUploading || !boardId
                }
                className="flex-1 md:flex-none relative"
                innerClassName="flex items-center justify-center"
              >
                <span>Link</span>
                <AnimatePresence>
                  {selectedExisting.size > 0 && (
                    <motion.span
                      className="flex items-center justify-center"
                      initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                      animate={{ width: "auto", opacity: 1, marginLeft: 8 }}
                      exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                    >
                      <Badge
                        count={selectedExisting.size}
                        action="info"
                        className=" bg-primary-400 border-primary-600/50 text-primary-100 dark:bg-primary-300 dark:border-primary-300 dark:text-primary-800 static"
                      />
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            )}
          </div>
        </div>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

export { AddMediaModal };
